import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button, Paper } from '@mui/material';
import { publicInterviewService } from '../services/api';
import LiveKitRoomView from '../components/LiveKitRoomView';

interface InterviewDetail {
  candidateName: string;
  position: string;
  scheduledAt: string;
  durationMinutes: number;
  status: string;
}

const reasonText = (reason?: string) => {
  switch (reason) {
    case 'LinkNotFound': return 'This interview link is not valid.';
    case 'InterviewCancelled': return 'This interview has been cancelled.';
    case 'InterviewCompleted': return 'This interview has already taken place.';
    case 'TooEarly': return 'This link isn’t active yet — it opens 15 minutes before your scheduled time.';
    case 'TooLate': return 'This interview’s time window has passed.';
    case 'LiveKitDisabled': return 'The video call service is temporarily unavailable. Please contact HR.';
    default: return 'This link is not currently active.';
  }
};

const formatIst = (value: string) => {
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return value;
  const [y, m, d] = datePart.split('-');
  const [hh, mm] = timePart.split(':');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  let hour = Number(hh);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${dateLabel} at ${hour}:${mm} ${suffix} IST`;
};

// The one page in this app a candidate ever opens without an HRMS account — reached only via the
// opaque link emailed by InterviewScheduling.tsx (/interview/:token). Deliberately rendered
// outside PrivateRoute/the authenticated app shell in App.tsx: no login, no sidebar, no HRMS
// branding chrome beyond what's needed to join the call.
const CandidateInterviewJoin: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [detail, setDetail] = useState<InterviewDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [session, setSession] = useState<{ token: string; serverUrl: string } | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [left, setLeft] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    publicInterviewService.getDetail(token)
      .then(setDetail)
      .catch(() => setDetailError('This interview link is not valid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleJoin = useCallback(async () => {
    if (!token) return;
    setJoining(true);
    setJoinError(null);
    try {
      const result = await publicInterviewService.join(token);
      if (result.canJoin && result.token && result.serverUrl) {
        setSession({ token: result.token, serverUrl: result.serverUrl });
      } else {
        setJoinError(reasonText(result.reason));
      }
    } catch {
      setJoinError('Could not connect. Please check your internet connection and try again.');
    } finally {
      setJoining(false);
    }
  }, [token]);

  const handleDisconnected = () => {
    setSession(null);
    setLeft(true);
  };

  if (session) {
    return (
      <Box sx={{ height: '100vh', bgcolor: 'black' }}>
        <LiveKitRoomView token={session.token} serverUrl={session.serverUrl} onDisconnected={handleDisconnected} fullPage />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Mqbit Technologies — Interview</Typography>

        {loading && <CircularProgress />}

        {!loading && detailError && <Alert severity="warning">{detailError}</Alert>}

        {!loading && !detailError && detail && (
          <>
            <Typography variant="body1" sx={{ mb: 0.5 }}>Hi {detail.candidateName},</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your interview for <strong>{detail.position}</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>{formatIst(detail.scheduledAt)}</Typography>

            {left && <Alert severity="info" sx={{ mb: 2 }}>You left the call.</Alert>}
            {joinError && <Alert severity="warning" sx={{ mb: 2 }}>{joinError}</Alert>}

            <Button variant="contained" size="large" onClick={handleJoin} disabled={joining}>
              {joining ? 'Connecting...' : left ? 'Rejoin Interview' : 'Join Interview'}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CandidateInterviewJoin;
